import {
  createSlice,
  PayloadAction,
  createEntityAdapter,
  createSelector,
} from '@reduxjs/toolkit';
import {
  Vignette,
  VignetteAnswerOption,
  VignetteResponse,
} from '../../types/Vignette';
import { UserExam } from '../../types/User';
import { RootState } from '../../store/store';
import { getScoringVignetteResponse } from '../../utils/vignetteResponses';

type VignettesStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

// No sortComparer: the backend sorts vignettes.
const vignettesAdapter = createEntityAdapter<Vignette>();

interface VignetteState extends ReturnType<
  typeof vignettesAdapter.getInitialState
> {
  status: VignettesStatus;
  error: string | null;
  blockId: string | null;
}

const initialState: VignetteState = vignettesAdapter.getInitialState({
  status: 'idle',
  error: null,
  blockId: null,
});

const hasResponseId = (id?: string | null) =>
  Boolean(id && id.trim().length > 0);

const normalizeAnswerLetter = (answer?: string | null) =>
  (answer ?? '').trim().toUpperCase();

const responsesRepresentSameWrite = (
  optimistic: VignetteResponse,
  server: VignetteResponse
) =>
  !hasResponseId(optimistic.id) &&
  hasResponseId(server.id) &&
  Boolean(optimistic.isRetry) === Boolean(server.isRetry) &&
  normalizeAnswerLetter(optimistic.answer) ===
    normalizeAnswerLetter(server.answer) &&
  (!optimistic.blockId ||
    !server.blockId ||
    optimistic.blockId === server.blockId);

const mergeVignetteResponses = (
  existing?: VignetteResponse[],
  incoming?: VignetteResponse[]
): VignetteResponse[] | undefined => {
  if (incoming === undefined) {
    return existing?.length ? [...existing] : undefined;
  }
  if (!incoming.length) {
    return [];
  }
  if (!existing?.length) {
    return [...incoming];
  }

  const remainingExisting = [...existing];

  const reconciledIncoming = incoming.map((serverResponse) => {
    const optimisticIndex = remainingExisting.findIndex((response) =>
      responsesRepresentSameWrite(response, serverResponse)
    );

    if (optimisticIndex < 0) {
      return serverResponse;
    }

    const [optimisticResponse] = remainingExisting.splice(optimisticIndex, 1);

    // A late draft response may race a newer optimistic submit. Adopt the
    // server identity and numbering without reverting the submitted state.
    if (optimisticResponse.isSubmitted && !serverResponse.isSubmitted) {
      return {
        ...optimisticResponse,
        ...serverResponse,
        isSubmitted: true,
        submissionDate: optimisticResponse.submissionDate,
      };
    }

    return serverResponse;
  });

  const byAttempt = new Map<string, VignetteResponse>();

  const upsertResponse = (response: VignetteResponse) => {
    const attemptNumber = response.attemptNumber ?? 1;
    const key = `${attemptNumber}-${Boolean(response.isRetry)}`;
    const current = byAttempt.get(key);
    if (!current) {
      byAttempt.set(key, response);
      return;
    }
    const currentHasId = hasResponseId(current.id);
    const nextHasId = hasResponseId(response.id);

    // Never allow a stale draft save to overwrite an already-submitted attempt.
    if (current.isSubmitted && !response.isSubmitted) {
      if (nextHasId && !currentHasId) {
        byAttempt.set(key, {
          ...current,
          id: response.id,
        });
      }
      return;
    }

    const currentTime = new Date(
      current.updatedAt || current.createdAt
    ).getTime();

    const nextTime = new Date(
      response.updatedAt || response.createdAt
    ).getTime();

    if (nextHasId && !currentHasId && nextTime < currentTime) {
      byAttempt.set(key, {
        ...current,
        id: response.id,
      });

      return;
    }

    if (nextTime >= currentTime) {
      byAttempt.set(
        key,
        !nextHasId && currentHasId
          ? {
              ...response,
              id: current.id,
            }
          : response
      );
    }
  };

  reconciledIncoming.forEach(upsertResponse);
  remainingExisting.forEach(upsertResponse);

  return Array.from(byAttempt.values()).sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );
};

const vignettesSlice = createSlice({
  name: 'vignettes',
  initialState,
  reducers: {
    vignettesLoading: (state) => {
      state.status = 'loading';
      state.error = null;
    },
    setVignettes: (state, action: PayloadAction<Vignette[]>) => {
      vignettesAdapter.setAll(state, action.payload);
      state.status = 'succeeded';
      state.blockId = null;
      state.error = null;
    },
    setVignettesForBlock: (
      state,
      action: PayloadAction<{ blockId: string; vignettes: Vignette[] }>
    ) => {
      vignettesAdapter.setAll(state, action.payload.vignettes);
      state.status = 'succeeded';
      state.blockId = action.payload.blockId;
      state.error = null;
    },
    vignetteReceived: (state, action: PayloadAction<Vignette>) => {
      const existing = state.entities[action.payload.id];

      const mergedResponses = mergeVignetteResponses(
        existing?.responses,
        action.payload.responses
      );

      vignettesAdapter.upsertOne(state, {
        ...action.payload,
        responses: mergedResponses,
      });

      state.status = 'succeeded';
      state.error = null;
    },
    shiftVignette: (state) => {
      const allVignettes = vignettesAdapter.getSelectors().selectAll(state);
      if (allVignettes.length > 0) {
        vignettesAdapter.removeOne(state, allVignettes[0].id);
      }
    },
    vignetteDeleted: (state, action: PayloadAction<string>) => {
      vignettesAdapter.removeOne(state, action.payload);
      state.status = 'succeeded';
      state.error = null;
    },
    vignettesFailed: (state, action: PayloadAction<string | null>) => {
      state.status = 'failed';
      state.error = action.payload ?? 'Failed to load vignettes';
    },
    clearVignettes: (state) => {
      vignettesAdapter.removeAll(state);
      state.status = 'idle';
      state.error = null;
      state.blockId = null;
    },
    replaceVignetteById: (
      state,
      action: PayloadAction<{
        vignetteId: string;
        newYieldNumber: number;
        newFirstSessionOrd: number;
        newExams: UserExam[];
      }>
    ) => {
      const { vignetteId, newYieldNumber, newFirstSessionOrd, newExams } =
        action.payload;

      const existingVignette = state.entities[vignetteId];
      if (existingVignette) {
        const updatedVignette = {
          ...existingVignette,
          yield: newYieldNumber,
          firstSessionOrd: newFirstSessionOrd,
          exams: newExams,
        };

        vignettesAdapter.updateOne(state, {
          id: vignetteId,
          changes: updatedVignette,
        });
      }
    },
    replaceVignetteContentById: (
      state,
      action: PayloadAction<{
        vignetteId: string;
        contentId: string;
        newQuestion: string;
        newExplanation: string;
        newExhibit: string;
        newLearningObjective: string;
        newAnswerOptions: VignetteAnswerOption[];
      }>
    ) => {
      const {
        vignetteId,
        contentId,
        newQuestion,
        newExplanation,
        newExhibit,
        newLearningObjective,
        newAnswerOptions,
      } = action.payload;

      const existingVignette = state.entities[vignetteId];
      if (existingVignette) {
        const sourceContents = existingVignette.contents ?? [];

        const contentIndex = sourceContents.findIndex(
          (content) => content.id === contentId
        );

        if (contentIndex !== -1) {
          const updatedContent = {
            ...sourceContents[contentIndex],
            question: newQuestion,
            explanation: newExplanation,
            exhibit: newExhibit,
            learningObjective: newLearningObjective,
            answerOptions: newAnswerOptions,
          };

          const clonedContents = [...sourceContents];
          clonedContents[contentIndex] = updatedContent;

          vignettesAdapter.updateOne(state, {
            id: vignetteId,
            changes: {
              contents: clonedContents,
            },
          });
        }
      }
    },
    addVignetteResponse: (
      state,
      action: PayloadAction<{ vignetteId: string; response: VignetteResponse }>
    ) => {
      const { vignetteId, response } = action.payload;
      const existingVignette = state.entities[vignetteId];
      if (existingVignette) {
        const existingResponses = existingVignette.responses ?? [];
        const attemptNumber = response.attemptNumber ?? 1;

        let responseIndex = hasResponseId(response.id)
          ? existingResponses.findIndex(
              (existingResponse) => existingResponse.id === response.id
            )
          : -1;

        if (responseIndex < 0) {
          responseIndex = existingResponses.findIndex(
            (existingResponse) =>
              (existingResponse.attemptNumber ?? 1) === attemptNumber &&
              Boolean(existingResponse.isRetry) === Boolean(response.isRetry)
          );
        }

        const updatedResponses = [...existingResponses];
        let resolvedResponse = response;

        if (responseIndex >= 0) {
          const existingResponse = updatedResponses[responseIndex];
          if (existingResponse.isSubmitted && !response.isSubmitted) {
            resolvedResponse = {
              ...existingResponse,
              id: hasResponseId(existingResponse.id)
                ? existingResponse.id
                : response.id,
            };
          } else {
            resolvedResponse = {
              ...existingResponse,
              ...response,
              id: hasResponseId(response.id)
                ? response.id
                : existingResponse.id,
            };
          }
          updatedResponses[responseIndex] = resolvedResponse;
        } else {
          updatedResponses.push(resolvedResponse);
        }

        updatedResponses.sort((a, b) => a.createdAt.localeCompare(b.createdAt));

        vignettesAdapter.updateOne(state, {
          id: vignetteId,
          changes: {
            response: resolvedResponse,
            responses: updatedResponses,
          },
        });

        state.status = 'succeeded';
        state.error = null;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(logout, (state) => {
      vignettesAdapter.removeAll(state);
      state.status = 'idle';
      state.error = null;
      state.blockId = null;
    });
  },
});

export const {
  vignettesLoading,
  setVignettes,
  setVignettesForBlock,
  vignetteReceived,
  vignettesFailed,
  clearVignettes,
  addVignetteResponse,
} = vignettesSlice.actions;

export const vignettesSelectors = vignettesAdapter.getSelectors(
  (state: { vignettes: VignetteState }) =>
    state.vignettes || vignettesAdapter.getInitialState()
);

export const selectVignettesBlockId = (state: RootState) =>
  state.vignettes.blockId;

// Counted against the block these vignettes were loaded for (ORA-465), so a
// repeat of a variant answered in an earlier block is not counted as already
// answered in this one.
export const selectSubmittedVignettesCount = createSelector(
  [vignettesSelectors.selectAll, selectVignettesBlockId],
  (vignettes: Vignette[], blockId: string | null) => {
    return vignettes.filter((vignette) => {
      return Boolean(
        getScoringVignetteResponse(vignette, blockId)?.isSubmitted
      );
    }).length;
  }
);

export const selectAnsweredVignettesCount = createSelector(
  [vignettesSelectors.selectAll, selectVignettesBlockId],
  (vignettes: Vignette[], blockId: string | null) => {
    return vignettes.filter((vignette) => {
      return Boolean(getScoringVignetteResponse(vignette, blockId)?.answer);
    }).length;
  }
);

// Selector to get the active vignette
export const selectActiveVignette = (state: RootState) => {
  const vignettes = vignettesSelectors.selectAll(state);
  const activeQuestionIndex = state.studyPhase.activeQuestionIndex;
  return vignettes[activeQuestionIndex] || null;
};

export default vignettesSlice.reducer;

// Import the logout action from authSlice
import { logout } from '../auth/authSlice';
